import { useEffect, useRef, useState, type CSSProperties } from "react";

/**
 * MagnifierCarousel
 * A carousel of images with a liquid-glass magnifier lens that follows the cursor.
 * Pass image pairs: `display` (visible img, optimized) and `hires` (lens source).
 * `hires` is lazy-loaded on first hover and falls back to `display` if omitted.
 */
export type MagnifierImage = { display: string; hires?: string };

export default function MagnifierCarousel({
  images = [],
  zoom = 3,
}: {
  images?: Array<MagnifierImage | string>;
  zoom?: number;
}) {
  const normalized: MagnifierImage[] = images.map((i) =>
    typeof i === "string" ? { display: i } : i,
  );

  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const lensRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const hiresLoadedRef = useRef<Record<number, { w: number; h: number; url: string }>>({});

  const LENS_W = 228;
  const LENS_H = 120;

  const applyBackground = () => {
    const img = imgRef.current;
    const lens = lensRef.current;
    if (!img || !lens) return;
    const rect = img.getBoundingClientRect();
    rectRef.current = rect;

    const cached = hiresLoadedRef.current[current];
    const url = cached?.url ?? normalized[current]?.display;
    if (!url) return;
    lens.style.backgroundImage = `url(${url})`;
    // Scale background so that on-screen pixels = source pixels * zoom.
    // Background-size mirrors the displayed image size * zoom; sharpness
    // comes from the hi-res source backing those pixels.
    lens.style.backgroundSize = `${rect.width * zoom}px ${rect.height * zoom}px`;
  };

  const preloadHires = (index: number) => {
    const item = normalized[index];
    if (!item?.hires || hiresLoadedRef.current[index]) return;
    const im = new Image();
    im.onload = () => {
      hiresLoadedRef.current[index] = {
        w: im.naturalWidth,
        h: im.naturalHeight,
        url: item.hires!,
      };
      if (index === current) applyBackground();
    };
    im.src = item.hires;
  };

  useEffect(() => {
    const onResize = () => applyBackground();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, current]);

  const show = (i: number) => {
    const next = (i + normalized.length) % normalized.length;
    setVisible(false);
    setCurrent(next);
  };

  const handleEnter = () => {
    preloadHires(current);
    applyBackground();
    setVisible(true);
  };

  const handleMove = (e: React.MouseEvent) => {
    const rect = rectRef.current;
    const lens = lensRef.current;
    if (!rect || !lens) return applyBackground();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    lens.style.left = `${x}px`;
    lens.style.top = `${y}px`;
    lens.style.backgroundPosition = `${-(x * zoom - LENS_W / 2)}px ${-(y * zoom - LENS_H / 2)}px`;
  };

  if (!normalized.length) return null;

  return (
    <div className="hidden md:block" style={{ maxWidth: 1100, width: "100%", margin: "0 auto" }}>
      <div
        ref={stageRef}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setVisible(false)}
        onMouseMove={handleMove}
        style={{
          position: "relative",
          width: "100%",
          lineHeight: 0,
          cursor: "none",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <img
          ref={imgRef}
          src={normalized[current].display}
          alt={`Slide ${current + 1}`}
          draggable={false}
          onLoad={applyBackground}
          style={{ width: "100%", height: "auto", display: "block", userSelect: "none" }}
        />

        <div
          ref={lensRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: LENS_W,
            height: LENS_H,
            pointerEvents: "none",
            opacity: visible ? 1 : 0,
            transform: "translate(-50%, -50%)",
            transition: "opacity 0.14s ease",
            zIndex: 5,
            backgroundRepeat: "no-repeat",
            // Binoculars shape: two overlapping circles
            WebkitMaskImage:
              "radial-gradient(circle at 26% 50%, #000 49%, transparent 50%), radial-gradient(circle at 74% 50%, #000 49%, transparent 50%)",
            maskImage:
              "radial-gradient(circle at 26% 50%, #000 49%, transparent 50%), radial-gradient(circle at 74% 50%, #000 49%, transparent 50%)",
            filter:
              "drop-shadow(0 8px 16px rgba(0,0,0,0.45)) drop-shadow(0 2px 4px rgba(0,0,0,0.35))",
          }}
        >
          {/* highlight + rim per barrel */}
          {[26, 74].map((cx) => (
            <span
              key={cx}
              style={{
                position: "absolute",
                left: `${cx}%`,
                top: "50%",
                width: LENS_H,
                height: LENS_H,
                transform: "translate(-50%, -50%)",
                borderRadius: "50%",
                background:
                  "radial-gradient(120% 80% at 28% 18%, rgba(255,255,255,0.45), rgba(255,255,255,0) 45%), radial-gradient(90% 70% at 78% 88%, rgba(255,255,255,0.18), rgba(255,255,255,0) 55%)",
                boxShadow:
                  "inset 0 0 0 1px rgba(255,255,255,0.55), inset 0 0 0 6px rgba(255,255,255,0.10), inset 0 6px 18px rgba(255,255,255,0.30), inset 0 -10px 22px rgba(0,0,0,0.30), inset 0 0 14px 2px rgba(255,255,255,0.22)",
                mixBlendMode: "screen",
                pointerEvents: "none",
              }}
            />
          ))}
        </div>

        {normalized.length > 1 && (
          <>
            <button onClick={() => show(current - 1)} aria-label="Previous image" style={navStyle("left")}>
              &#8249;
            </button>
            <button onClick={() => show(current + 1)} aria-label="Next image" style={navStyle("right")}>
              &#8250;
            </button>
          </>
        )}
      </div>

      {normalized.length > 1 && (
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 16 }}>
          {normalized.map((item, i) => (
            <img
              key={i}
              src={item.display}
              alt={`Thumbnail ${i + 1}`}
              onClick={() => show(i)}
              style={{
                width: 96,
                height: 64,
                objectFit: "cover",
                borderRadius: 6,
                cursor: "pointer",
                opacity: i === current ? 1 : 0.5,
                border: i === current ? "2px solid #6fb3ff" : "2px solid transparent",
                transition: "opacity 0.15s ease, border-color 0.15s ease",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function navStyle(side: "left" | "right"): CSSProperties {
  return {
    position: "absolute",
    top: "50%",
    [side]: 14,
    transform: "translateY(-50%)",
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: "rgba(20,20,22,0.55)",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 6,
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  };
}
